<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHelpStore, type HelpContent } from '@/store/help'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { IconField } from 'primevue'

interface Props {
  helpId?: string
  moduleString?: string
  content?: HelpContent
}

const props = defineProps<Props>()
const helpStore = useHelpStore()
const { t, te } = useI18n()

const baseKey = computed<string | null>(() => {
  if (!props.helpId) return null
  return props.moduleString ? `${props.moduleString}.help.${props.helpId}` : `help.${props.helpId}`
})

function resolveKey(suffix: string): string | undefined {
  const key = baseKey.value
  if (!key) return undefined
  const fullKey = `${key}.${suffix}`
  return te(fullKey) ? String(t(fullKey)) : undefined
}

const resolvedContent = computed<HelpContent>(() => {
  const direct = props.content ?? {}
  return {
    title: direct.title ?? resolveKey('title'),
    helpText: direct.helpText ?? resolveKey('helpText'),
    imgSrc: direct.imgSrc ?? resolveKey('imgSrc'),
    docsLink: direct.docsLink ?? resolveKey('docsLink')
  }
})

const identifier = computed(() => baseKey.value ?? `inline:${resolvedContent.value.title ?? ''}`)

const hasContent = computed(() => {
  const c = resolvedContent.value
  return Boolean(c.title || c.helpText || c.imgSrc || c.docsLink)
})

function onClick(event: Event) {
  event.preventDefault()
  event.stopPropagation()
  if (!hasContent.value) return
  helpStore.toggle(identifier.value, resolvedContent.value)
}
</script>

<template>
  <Button variant="text" class="help-toggler" aria-label="help" @click="onClick">
    <template #icon>
      <FontAwesomeIcon :icon="['fas', 'question-circle']" class="text-gray-400" />
    </template>
  </Button>
</template>

<style scoped></style>
